import type {
  AutocompleteField,
  FieldWarningRule,
  FormField,
  FormSection,
  NumericField,
  SelectField,
} from '@/screens/ReportCreation/reportCreation.types';
import {
  isAbsoluteDependency,
  normalizeDependencyName,
} from '@/screens/ReportCreation/utils/conditionalVisibility.utils';

const joinFieldPath = (prefix: string, fieldId: string): string =>
  prefix ? `${prefix}.${fieldId}` : fieldId;

const applyDependencyPrefix = (dependency: string, prefix: string): string => {
  const normalized = normalizeDependencyName(dependency);
  return isAbsoluteDependency(dependency)
    ? normalized
    : joinFieldPath(prefix, normalized);
};

const prefixConditional = (
  conditional: FormField['conditional'],
  prefix: string,
): FormField['conditional'] => {
  if (!conditional) {
    return undefined;
  }

  return {
    ...conditional,
    dependsOn: applyDependencyPrefix(conditional.dependsOn, prefix),
  };
};

const prefixNumericField = (
  field: NumericField,
  prefix: string,
): NumericField => {
  return {
    ...field,
    id: joinFieldPath(prefix, field.id),
    conditional: prefixConditional(field.conditional, prefix),
    autoCalculate: field.autoCalculate
      ? {
          ...field.autoCalculate,
          dependsOn: field.autoCalculate.dependsOn.map((dependency) =>
            applyDependencyPrefix(dependency, prefix),
          ),
        }
      : undefined,
  };
};

const prefixSelectField = (field: SelectField, prefix: string): SelectField => {
  return {
    ...field,
    id: joinFieldPath(prefix, field.id),
    conditional: prefixConditional(field.conditional, prefix),
    dynamicOptions: field.dynamicOptions
      ? {
          dependsOn: applyDependencyPrefix(
            field.dynamicOptions.dependsOn,
            prefix,
          ),
          optionsByValue: field.dynamicOptions.optionsByValue,
        }
      : undefined,
  };
};

const prefixAutocompleteField = (
  field: AutocompleteField,
  prefix: string,
): AutocompleteField => {
  return {
    ...field,
    id: joinFieldPath(prefix, field.id),
    conditional: prefixConditional(field.conditional, prefix),
    dynamicOptions: field.dynamicOptions
      ? {
          dependsOn: applyDependencyPrefix(
            field.dynamicOptions.dependsOn,
            prefix,
          ),
          optionsByValue: field.dynamicOptions.optionsByValue,
        }
      : undefined,
  };
};

const prefixWarningRule = (
  warning: FieldWarningRule,
  prefix: string,
): FieldWarningRule => ({
  ...warning,
  dependsOn: warning.dependsOn?.map((dependency) =>
    applyDependencyPrefix(dependency, prefix),
  ),
  isVisible: (getValue) =>
    warning.isVisible((name) => getValue(applyDependencyPrefix(name, prefix))),
});

const prefixWarnings = (
  warnings: FieldWarningRule[] | undefined,
  prefix: string,
): FieldWarningRule[] | undefined =>
  warnings?.map((warning) => prefixWarningRule(warning, prefix));

const prefixField = (field: FormField, prefix: string): FormField => {
  if (field.type === 'numeric') {
    return {
      ...prefixNumericField(field, prefix),
      warnings: prefixWarnings(field.warnings, prefix),
    };
  }

  if (field.type === 'select') {
    return {
      ...prefixSelectField(field, prefix),
      warnings: prefixWarnings(field.warnings, prefix),
    };
  }

  if (field.type === 'autocomplete-search') {
    return {
      ...prefixAutocompleteField(field, prefix),
      warnings: prefixWarnings(field.warnings, prefix),
    };
  }

  return {
    ...field,
    id: joinFieldPath(prefix, field.id),
    conditional: prefixConditional(field.conditional, prefix),
    warnings: prefixWarnings(field.warnings, prefix),
  };
};

export const prefixFormSections = (
  sections: FormSection[],
  prefix: string,
): FormSection[] =>
  sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) => prefixField(field, prefix)),
  }));
